"use client"

import type { ReactNode } from "react"
import * as DrawerPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"

export type DrawerProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  className?: string
}

// Painel lateral MARK V sobre @radix-ui/react-dialog. `title` é obrigatório
// (acessibilidade Radix). Superfície clara do app; foco ring verde.
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
}: DrawerProps) {
  return (
    <DrawerPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DrawerPrimitive.Content
          className={cn(
            "fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-md flex-col gap-4 border-l border-markv-surface-200 bg-markv-surface-50 p-6 text-markv-text-700 shadow-markv-lg outline-none",
            "duration-200 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:animate-in data-[state=open]:slide-in-from-right",
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <DrawerPrimitive.Title className="font-display text-base font-bold text-markv-text-700">
                {title}
              </DrawerPrimitive.Title>
              {description ? (
                <DrawerPrimitive.Description className="text-sm text-markv-text-400">
                  {description}
                </DrawerPrimitive.Description>
              ) : null}
            </div>
            <DrawerPrimitive.Close
              aria-label="Fechar"
              className="rounded-md p-1 text-markv-text-400 transition-colors hover:text-markv-text-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-markv-primary-500"
            >
              <XIcon className="size-4" />
            </DrawerPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto text-sm text-markv-text-700">
            {children}
          </div>
          {footer ? (
            <div className="flex justify-end gap-2 border-t border-markv-surface-200 pt-4">
              {footer}
            </div>
          ) : null}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  )
}
